import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api, errMsg, cents } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import usePageMeta from "../hooks/usePageMeta";
import { track } from "../lib/analytics";

function SmsAlertsCard() {
  const [phone, setPhone] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/account/sms-preferences").then((r) => { setPhone(r.data.phone || ""); setOptIn(!!r.data.sms_opt_in); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/account/sms-preferences", { phone: phone || null, sms_opt_in: optIn });
      setPhone(data.phone || ""); setOptIn(!!data.sms_opt_in);
      toast.success(data.sms_opt_in ? "Text alerts on — we'll text you when your order is ready" : "Text alerts off");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!loaded) return null;
  return (
    <section className="card-paper p-5 mb-6" data-testid="account-sms-card">
      <h2 className="text-lg font-medium mb-2">Ready-for-pickup text alerts</h2>
      <p className="text-xs text-[#171715]/60 mb-3">Optional. We only text when your order is marked ready — never marketing. Reply STOP any time to opt out.</p>
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="account-sms-phone" type="tel" className="input-line" placeholder="+1 214 555 0134" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="account-sms-optin" type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />
          Text me when my order is ready
        </label>
        <button data-testid="account-sms-save" className="btn-ghost text-sm" onClick={save}>Save</button>
      </div>
    </section>
  );
}

function CancelPanel({ sub, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const doCancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/subscriptions/${sub.id}/cancel`, { reason: reason || null });
      track("subscription_cancel", {});
      if (data.status === "canceled") toast.success(data.message);
      else toast.warning(data.message);
      setOpen(false); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="account-cancel-subscription-button" className="btn-danger w-full sm:w-auto" onClick={() => setOpen(true)}>
        Cancel subscription
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div data-testid="cancel-confirmation-panel" className="relative bg-[#FBF6F1] border border-[#EBD9D0] p-6 max-w-md w-full">
            <h3 className="text-xl mb-2">Cancel this subscription?</h3>
            <p className="text-sm text-[#171715]/80 mb-2"><span className="font-medium">{sub.plan_label || sub.plan_key}</span> · {cents(sub.price_cents)} per pickup at {sub.location_name}.</p>
            <p className="text-sm text-[#171715]/75 mb-2">Cancellation is immediate in Stripe — no further charges. Already-paid pickups stay visible and remain eligible for their scheduled pickup under the refund policy (a separate refund request path exists).</p>
            <label className="text-xs font-medium block mt-3 mb-1" htmlFor="cancel-reason">Reason (optional — never required)</label>
            <textarea id="cancel-reason" data-testid="cancel-reason-input" className="input-line" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="flex gap-3 mt-4">
              <button data-testid="cancel-now-button" className="btn-danger flex-1" disabled={busy} onClick={doCancel}>{busy ? "Canceling…" : "Cancel now"}</button>
              <button data-testid="cancel-keep-button" className="btn-ghost flex-1" onClick={() => setOpen(false)}>Keep subscription</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SubCard({ sub, onRefresh }) {
  const [busy, setBusy] = useState("");
  const act = async (action) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/subscriptions/${sub.id}/${action}`);
      if (action === "skip") track("subscription_skip", {});
      if (action === "pause") track("subscription_pause", {});
      toast.success(data.message || "Done");
      onRefresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div data-testid={`account-subscription-card-${sub.id}`} className="card-paper p-5">
      <div className="flex flex-wrap justify-between gap-2 items-start">
        <div>
          <p className="font-medium">{sub.bottles} bottles · every {sub.interval_days} days · {cents(sub.price_cents)} per pickup</p>
          <p className="text-sm text-[#171715]/70">{sub.location_name} · {sub.window_label}</p>
        </div>
        <span data-testid={`subscription-status-${sub.id}`} className={`text-xs font-mono uppercase px-2 py-1 border ${sub.status === "active" ? "text-[#A35847] border-[#A35847]/40" : sub.status === "canceled" ? "text-[#9c3b2a] border-[#9c3b2a]/40" : "text-[#171715]/70 border-[#EBD9D0]"}`}>{sub.status}</span>
      </div>
      {sub.status === "cancel_pending" && <p className="text-xs text-[#9c3b2a] mt-2">Cancellation requested — waiting for Stripe confirmation. Local billing/production is blocked meanwhile; we never promise a charge is stopped until Stripe confirms.</p>}
      {sub.status !== "canceled" && (
        <dl className="text-sm mt-3 space-y-1">
          <div className="flex justify-between"><dt>Next pickup</dt><dd>{sub.next_pickup_date}</dd></div>
          <div className="flex justify-between"><dt>Next charge</dt><dd>{sub.next_charge_at ? new Date(sub.next_charge_at).toLocaleDateString("en-US", { timeZone: "America/Chicago" }) : "—"}</dd></div>
          <div className="flex justify-between"><dt>Flavors</dt><dd className="text-right">{sub.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</dd></div>
        </dl>
      )}
      <div className="flex flex-wrap gap-2 mt-4">
        {sub.status === "active" && <button data-testid="account-skip-delivery-button" className="btn-ghost text-sm" disabled={!!busy} onClick={() => act("skip")}>{busy === "skip" ? "…" : "Skip next cycle"}</button>}
        {sub.status === "active" && <button data-testid="account-pause-subscription-button" className="btn-ghost text-sm" disabled={!!busy} onClick={() => act("pause")}>{busy === "pause" ? "…" : "Pause"}</button>}
        {sub.status === "paused" && <button data-testid="account-resume-subscription-button" className="btn-ghost text-sm" disabled={!!busy} onClick={() => act("resume")}>{busy === "resume" ? "…" : "Resume"}</button>}
        {["active", "paused", "cancel_pending"].includes(sub.status) && <CancelPanel sub={sub} onDone={onRefresh} />}
      </div>
    </div>
  );
}

function FavoriteStacks({ onUse }) {
  const [stacks, setStacks] = useState(null);
  const load = () => api.get("/account/stacks").then((r) => setStacks(r.data)).catch(() => setStacks([]));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    try { await api.delete(`/account/stacks/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!stacks || stacks.length === 0) return null;
  return (
    <section className="mb-8" data-testid="account-favorite-stacks">
      <h2 className="text-2xl mb-4">Favourite stacks</h2>
      <div className="card-paper divide-y divide-[#EBD9D0]">
        {stacks.map((s) => (
          <div key={s.id} className="p-4 text-sm flex flex-wrap items-center justify-between gap-2" data-testid={`account-stack-${s.id}`}>
            <div>
              <p className="font-medium">{s.name}</p>
              <p className="text-[#171715]/70">{s.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid={`account-stack-use-${s.id}`} className="btn-ink text-sm px-4 min-h-[44px]" onClick={() => onUse(s)}>Add to cart</button>
              <button data-testid={`account-stack-delete-${s.id}`} aria-label={`Delete ${s.name}`} className="btn-ghost text-sm min-h-[44px]" onClick={() => del(s.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#171715]/55 mt-2">Save new favourites from the cart page before checkout.</p>
    </section>
  );
}

export default function Account() {
  usePageMeta("My Account | Tiff. Wellness Co.", "Manage your tiff. wellness co. pickups, orders and subscriptions.", { robots: "noindex,nofollow" });
  const { user, logout } = useAuth();
  const { replace } = useCart();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const reorder = (o) => {
    replace(Object.fromEntries(o.items.map((i) => [i.slug, i.qty])));
    toast.success("Stack loaded into your cart");
    navigate("/cart");
  };

  const refresh = () => api.get("/account/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    if (user === false) navigate("/auth?next=/account");
    if (user && user !== false) refresh();
  }, [user]); // eslint-disable-line

  const portal = async () => {
    try {
      const { data: d } = await api.post("/account/portal", { origin_url: window.location.origin });
      window.location.href = d.url;
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!user || user === false || !data) return <div className="py-20 text-center text-sm">Loading…</div>;
  const np = data.next_pickup;

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
        <h1 className="text-4xl">Hi{user.name ? `, ${user.name.split(" ")[0]}` : ""}</h1>
        <button data-testid="account-logout-button" className="btn-ghost text-sm" onClick={async () => { await logout(); navigate("/"); }}>Sign out</button>
      </div>

      <section className="card-paper p-5 mb-6" data-testid="account-next-pickup-card">
        <p className="eyebrow mb-2">Next pickup</p>
        {np ? (
          <div className="flex flex-wrap justify-between gap-4">
            <div>
              <p className="font-medium">{np.pickup.location_name}</p>
              <p className="text-sm text-[#171715]/75">{np.pickup.date} · {np.pickup.window_label}</p>
              <p className="text-sm text-[#171715]/75">{np.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</p>
              <p className="text-xs text-[#171715]/55 mt-1">{np.pickup.instructions}</p>
            </div>
            {np.pickup_code && (
              <div data-testid="account-next-pickup-code-badge" className="border border-[#A35847] p-3 self-start text-center">
                <QRCodeSVG data-testid="account-pickup-qr" value={np.pickup_code} size={96} bgColor="#ffffff" fgColor="#171715" aria-label={`Pickup QR code ${np.pickup_code}`} />
                <p className="font-mono text-lg mt-2">{np.pickup_code}</p>
                <p className="text-[10px] text-[#171715]/55">Show QR or code at the counter</p>
              </div>
            )}
          </div>
        ) : <p className="text-sm text-[#171715]/70">No upcoming paid pickups. <Link to="/build-your-stack" className="underline">Build a stack</Link>.</p>}
      </section>

      <section className="mb-8">
        <h2 className="text-2xl mb-4">Subscriptions</h2>
        {data.subscriptions.length === 0 ? (
          <p className="text-sm text-[#171715]/70 card-paper p-5">No subscriptions. <Link to="/subscriptions" className="underline">Explore plans</Link>.</p>
        ) : (
          <div className="space-y-4">{data.subscriptions.map((s) => <SubCard key={s.id} sub={s} onRefresh={refresh} />)}</div>
        )}
      </section>

      <FavoriteStacks onUse={reorder} />

      <section className="mb-8">
        <h2 className="text-2xl mb-4">Order history</h2>
        <div className="card-paper divide-y divide-[#EBD9D0]" data-testid="account-order-history">
          {data.orders.filter((o) => o.payment_status !== "unpaid").map((o, idx) => (
            <div key={o.id} className="p-4 text-sm flex flex-wrap justify-between gap-2" data-testid={`account-order-${o.order_number}`}>
              <div>
                <p className="font-medium">{o.order_number} <span className="font-normal text-[#171715]/60">· {o.pickup.date}</span></p>
                <p className="text-[#171715]/70">{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</p>
                {idx === 0 && (
                  <button data-testid="account-reorder-button" className="btn-ink text-sm mt-3 px-4 min-h-[44px]" onClick={() => reorder(o)}>
                    Reorder this stack
                  </button>
                )}
              </div>
              <div className="text-right">
                <p className="font-mono">{cents(o.total_cents)}</p>
                <p className="text-xs font-mono uppercase text-[#171715]/60">{o.payment_status} · {o.fulfillment_status}</p>
              </div>
            </div>
          ))}
          {data.orders.filter((o) => o.payment_status !== "unpaid").length === 0 && <p className="p-4 text-sm text-[#171715]/60">No orders yet.</p>}
        </div>
      </section>

      <SmsAlertsCard />

      <section className="flex flex-wrap gap-3">
        <button data-testid="account-payment-portal-button" className="btn-ghost text-sm" onClick={portal}>Payment methods &amp; invoices (Stripe portal)</button>
        <Link to="/email-preferences" data-testid="account-email-preferences-link" className="btn-ghost text-sm">Email preferences</Link>
        <Link to="/contact" data-testid="account-refund-request-link" className="btn-ghost text-sm">Support / refund request</Link>
      </section>
    </div>
  );
}
